function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)',
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      // Submenu summaries are hidden, so this branch shouldn't be reached
      event.preventDefault();
    }
  }

  openMenuDrawer(summaryElement) {
    // Re-open all submenu details
    this.mainDetailsToggle
      .querySelectorAll('.menu-drawer__menu details, .menu-drawer__submenu details')
      .forEach((details) => {
        details.setAttribute('open', '');
      });
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    // Only close the main menu drawer, not the submenu details
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      // Skip submenu details - they should stay open
      if (details.id && details.id.includes('menu-drawer-menu-item')) return;
      if (details.id && details.id.includes('Details-menu-drawer-') && details.id !== 'Details-menu-drawer-container')
        return;
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`),
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // Chrome clones template elements without syncing the muted IDL attribute,
        // so video.muted stays false even though muted="muted" is in the HTML.
        // Chrome's autoplay policy checks the property, not the attribute.
        deferredElement.muted = true;
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset',
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId,
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' },
    );
    this.observer.observe(this);
  }

  async loadRecommendations(productId) {
    if (this.dataset.sisterName) {
      try {
        if (await this.loadSisterProducts(productId)) return;
      } catch (error) {
        console.error(error);
      }
    }

    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  async loadSisterProducts(productId) {
    const searchUrl = new URL(this.dataset.sisterUrl, window.location.origin);
    searchUrl.searchParams.set('q', `title:${this.dataset.sisterName}*`);
    searchUrl.searchParams.set('type', 'product');
    searchUrl.searchParams.set('options[unavailable_products]', 'show');
    searchUrl.searchParams.set('section_id', this.dataset.sectionId);

    const limit = Number(this.dataset.productsToShow);
    const cards = [];
    const productIds = new Set([String(productId)]);
    const visitedPages = new Set();
    let nextUrl = searchUrl.href;
    let content;

    while (nextUrl && !visitedPages.has(nextUrl)) {
      visitedPages.add(nextUrl);
      const response = await fetch(nextUrl);
      if (!response.ok) throw new Error(`Sister product search failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const recommendations = html.querySelector('product-recommendations');
      if (!recommendations) return false;
      if (!content && recommendations.querySelector('.product-grid')) content = recommendations;

      for (const card of recommendations.querySelectorAll('[data-sister-product-id]')) {
        const sisterProductId = card.dataset.sisterProductId;
        if (productIds.has(sisterProductId)) continue;
        productIds.add(sisterProductId);
        cards.push(card);
      }

      if (recommendations.dataset.nextPageUrl) {
        const nextPageUrl = new URL(recommendations.dataset.nextPageUrl, window.location.origin);
        nextPageUrl.searchParams.set('section_id', this.dataset.sectionId);
        nextUrl = nextPageUrl.href;
      } else {
        nextUrl = null;
      }
    }

    if (!cards.length || !content) return false;
    if (cards.length > limit) {
      for (let remainingIndex = cards.length - 1; remainingIndex > 0; remainingIndex--) {
        const randomIndex = Math.floor(Math.random() * (remainingIndex + 1));
        [cards[remainingIndex], cards[randomIndex]] = [cards[randomIndex], cards[remainingIndex]];
      }
    }
    const cardStyles = content.querySelectorAll('link[rel="stylesheet"]');
    content.querySelector('.product-grid').replaceChildren(...cards.slice(0, limit));
    content.prepend(...cardStyles);
    this.replaceChildren(...content.childNodes);
    this.classList.add('product-recommendations--loaded');
    return true;
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = 'cart-performance';

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }
}

// Style "Follow on Shop" button
document.addEventListener('DOMContentLoaded', function () {
  const seasonalColor = getComputedStyle(document.documentElement).getPropertyValue('--seasonal-color').trim();

  function findAndStyleButtons() {
    let found = false;

    // 1. Check regular DOM for .bg-purple-primary and .gravity-button
    const purpleButtons = document.querySelectorAll('.bg-purple-primary');
    if (purpleButtons.length > 0) {
      purpleButtons.forEach((btn, i) => {
        btn.style.setProperty('background-color', seasonalColor, 'important');
      });
      found = true;
    }

    // Check for .gravity-button class
    const gravityButtons = document.querySelectorAll('.gravity-button');
    if (gravityButtons.length > 0) {
      gravityButtons.forEach((btn, i) => {
        btn.style.setProperty('background-color', seasonalColor, 'important');
      });
      found = true;
    }

    // Check for <gravity-button> custom element (has its own shadow DOM)
    const gravityElements = document.querySelectorAll('gravity-button');
    gravityElements.forEach((el, i) => {
      if (el.shadowRoot) {
        const innerButtons = el.shadowRoot.querySelectorAll('button, [role="button"]');
        innerButtons.forEach((btn) => {
          btn.style.setProperty('background-color', seasonalColor, 'important');
          found = true;
        });
        // Also style the host itself via adoptedStyleSheets or direct style
        const allInner = el.shadowRoot.querySelectorAll('*');
        allInner.forEach((inner) => {
          const bg = getComputedStyle(inner).backgroundColor;
          if (bg.includes('128') || bg.includes('124') || bg.includes('purple')) {
            inner.style.setProperty('background-color', seasonalColor, 'important');
            found = true;
          }
        });
      }
      // Also style the element itself if it has bg
      el.style.setProperty('background-color', seasonalColor, 'important');
    });

    // 2. Find ALL elements with shadow roots and check inside them
    const allElements = document.querySelectorAll('*');
    allElements.forEach((el) => {
      if (el.shadowRoot) {
        // Inject a style element into every shadow DOM to override purple styles
        if (!el.shadowRoot.querySelector('#nous-custom-style')) {
          const styleEl = document.createElement('style');
          styleEl.id = 'nous-custom-style';
          // Use nuclear option - override ALL purple-related backgrounds
          styleEl.textContent = `
            *[class*="purple"] {
              background-color: ${seasonalColor} !important;
              background: ${seasonalColor} !important;
            }
            div[class*="purple"] {
              background-color: ${seasonalColor} !important;
              background: ${seasonalColor} !important;
            }
            .bg-purple-primary {
              background-color: ${seasonalColor} !important;
              background: ${seasonalColor} !important;
            }
            *[class*="hover"][class*="purple"]:hover {
              background-color: ${seasonalColor} !important;
              background: ${seasonalColor} !important;
            }
            .group:hover *[class*="purple"] {
              background-color: ${seasonalColor} !important;
              background: ${seasonalColor} !important;
            }
            *[class*="gravity"] {
              background-color: ${seasonalColor} !important;
              background: ${seasonalColor} !important;
            }
          `;
          // Prepend instead of append for higher priority
          el.shadowRoot.prepend(styleEl);
        }

        const shadowPurple = el.shadowRoot.querySelectorAll('.bg-purple-primary');
        const shadowButtons = el.shadowRoot.querySelectorAll('button');

        shadowPurple.forEach((btn, i) => {
          btn.style.setProperty('background-color', seasonalColor, 'important');
          found = true;
        });

        // Also check for .gravity-button in shadow DOM
        const shadowGravity = el.shadowRoot.querySelectorAll('.gravity-button');
        shadowGravity.forEach((btn, i) => {
          btn.style.setProperty('background-color', seasonalColor, 'important');
          found = true;
        });

        // Also style any purple-colored button
        shadowButtons.forEach((btn) => {
          const computedBg = getComputedStyle(btn).backgroundColor;
          // Check for purple-ish colors (rgb values around 128-140, 90-100, 213-220)
          if (
            computedBg.includes('128') ||
            computedBg.includes('purple') ||
            btn.classList.contains('bg-purple-primary')
          ) {
            btn.style.setProperty('background-color', seasonalColor, 'important');
            found = true;
          }
        });
      }
    });

    // 3. Log all custom elements for debugging
    const customElements = document.querySelectorAll('[class*="shop"], shop-follow, shop-login-button, [id*="shop"]');
    customElements.forEach((el) => {});

    // 4. Find ALL gravity-related elements (any tag/class/id containing 'gravity')
    const allElems = document.querySelectorAll('*');
    allElems.forEach((el) => {
      const tag = el.tagName.toLowerCase();
      const cls = String(el.className || '');
      const id = typeof el.id === 'string' ? el.id : '';
      if (tag.includes('gravity') || cls.includes('gravity') || id.includes('gravity')) {
        el.style.setProperty('background-color', seasonalColor, 'important');
      }
      // Also check shadow DOM for gravity
      if (el.shadowRoot) {
        const shadowAll = el.shadowRoot.querySelectorAll('*');
        shadowAll.forEach((inner) => {
          const innerTag = inner.tagName.toLowerCase();
          const innerCls = String(inner.className || '');
          const innerId = typeof inner.id === 'string' ? inner.id : '';
          if (innerTag.includes('gravity') || innerCls.includes('gravity') || innerId.includes('gravity')) {
            inner.style.setProperty('background-color', seasonalColor, 'important');
          }
        });
      }
    });

    // 5. Check footer__follow-on-shop for nested elements with shadow DOM
    const followOnShopContainer = document.querySelector('.footer__follow-on-shop');
    if (followOnShopContainer) {
      const nestedElements = followOnShopContainer.querySelectorAll('*');
      nestedElements.forEach((el) => {
        // Helper function to force style and lock it
        const forceStyle = (element, color) => {
          if (!element) return;
          // Remove purple classes
          if (element.className && typeof element.className === 'string') {
            element.className = element.className
              .replace(/bg-purple[^\s]*/g, '')
              .replace(/group-hover_bg-purple[^\s]*/g, '');
          }
          // Set inline style
          element.style.cssText = `background-color: ${color} !important; background: ${color} !important;`;
          element.setAttribute('style', `background-color: ${color} !important; background: ${color} !important;`);
          // Try to lock the className property
          try {
            Object.defineProperty(element, 'className', {
              get: () => element.getAttribute('class') || '',
              set: (val) => {
                // Filter out purple classes when being set
                const filtered = String(val)
                  .replace(/bg-purple[^\s]*/g, '')
                  .replace(/group-hover_bg-purple[^\s]*/g, '');
                element.setAttribute('class', filtered);
              },
              configurable: true,
            });
          } catch (e) {
            /* ignore */
          }
        };

        // Also try targeting SHOP-FOLLOW-BUTTON directly
        if (el.tagName === 'SHOP-FOLLOW-BUTTON' && el.shadowRoot) {
          // NUCLEAR OPTION: Apply hue-rotate filter to shift purple to blue
          // Purple is ~270deg hue, our blue is ~200deg, so rotate by -70deg
          // Also try applying to the element itself
          el.style.setProperty('--shop-color-primary', seasonalColor, 'important');
          el.style.setProperty('--color-purple-primary', seasonalColor, 'important');

          // Target the background div by its structural classes (not purple which we remove)
          // The purple div has: absolute inset-y-0 -z-10 rounded-max
          const bgDiv = el.shadowRoot.querySelector('div[class*="absolute"][class*="inset-y-0"][class*="-z-10"]');
          if (bgDiv) {
            // Try hiding the purple div and creating our own
            bgDiv.style.setProperty('display', 'none', 'important');

            // Create a replacement div
            if (!el.shadowRoot.querySelector('#nous-bg-replacement')) {
              const replacement = document.createElement('div');
              replacement.id = 'nous-bg-replacement';
              replacement.style.cssText = `
                position: absolute;
                inset: 0;
                z-index: -10;
                border-radius: 9999px;
                background-color: ${seasonalColor};
                pointer-events: none;
              `;
              // Insert it in the same position
              bgDiv.parentNode.insertBefore(replacement, bgDiv);
            }
          }

          // Also hide any other purple divs found
          const purpleDiv = el.shadowRoot.querySelector('[class*="purple"]');
          if (purpleDiv && purpleDiv.id !== 'nous-bg-replacement') {
            purpleDiv.style.setProperty('display', 'none', 'important');
          }

          // Add MutationObserver on shadow DOM to catch re-renders
          if (!el.shadowRoot._nousObserverAdded) {
            el.shadowRoot._nousObserverAdded = true;
            const shadowObserver = new MutationObserver((mutations) => {
              mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                  const target = mutation.target;
                  const cls = String(target.className || '');
                  if (cls.includes('purple') && target.id !== 'nous-bg-replacement') {
                    target.style.setProperty('display', 'none', 'important');
                  }
                }
                // If new nodes added, check for purple
                if (mutation.type === 'childList' && mutation.addedNodes.length) {
                  mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) {
                      const cls = String(node.className || '');
                      if (cls.includes('purple') && node.id !== 'nous-bg-replacement') {
                        node.style.setProperty('display', 'none', 'important');
                      }
                    }
                  });
                }
              });
            });
            shadowObserver.observe(el.shadowRoot, {
              attributes: true,
              attributeFilter: ['class', 'style'],
              childList: true,
              subtree: true,
            });
          }
        }
        if (el.shadowRoot) {
          // Try modifying existing adoptedStyleSheets to hide purple
          try {
            const existingSheets = el.shadowRoot.adoptedStyleSheets || [];

            // Create a completely new stylesheet array - HIDE purple elements
            const ourSheet = new CSSStyleSheet();
            ourSheet.replaceSync(`
              /* NOUS: Hide purple elements completely */
              .bg-purple-primary,
              .bg-purple-d0,
              [class*="bg-purple"],
              div[class*="absolute"][class*="inset-y-0"][class*="-z-10"]:not(#nous-bg-replacement) {
                display: none !important;
              }
              /* Also try visibility */
              [class*="purple"]:not(#nous-bg-replacement) {
                visibility: hidden !important;
                opacity: 0 !important;
              }
            `);

            // Put our sheet LAST
            el.shadowRoot.adoptedStyleSheets = [...existingSheets, ourSheet];
          } catch (e) {}

          // First, find and modify the existing STYLE element in shadow DOM
          const existingStyle = el.shadowRoot.querySelector('style');
          if (existingStyle) {
            // Append our overrides to the existing style - HIDE purple
            existingStyle.textContent += `
              /* NOUS OVERRIDE - HIDE */
              .bg-purple-primary,
              .bg-purple-d0,
              [class*="bg-purple"],
              [class*="purple"],
              div[class*="absolute"][class*="inset-y-0"]:not(#nous-bg-replacement) {
                display: none !important;
                visibility: hidden !important;
              }
            `;
          }

          // Also inject a style element as fallback
          if (!el.shadowRoot.querySelector('#nous-custom-style')) {
            const styleEl = document.createElement('style');
            styleEl.id = 'nous-custom-style';
            styleEl.textContent = `
              /* NOUS: Hide purple and show nothing */
              *[class*="purple"]:not(#nous-bg-replacement),
              div[class*="absolute"][class*="inset-y-0"]:not(#nous-bg-replacement) {
                display: none !important;
                visibility: hidden !important;
              }
            `;
            el.shadowRoot.prepend(styleEl);
          }

          // Also directly hide any purple elements we find
          const shadowAll = el.shadowRoot.querySelectorAll('*');
          shadowAll.forEach((inner) => {
            const cls = String(inner.className || '');

            // Hide any element with purple class (except our replacement)
            if ((cls.includes('bg-purple') || cls.includes('purple')) && inner.id !== 'nous-bg-replacement') {
              inner.style.setProperty('display', 'none', 'important');
            }
          });
        }
      });
    }

    return found;
  }

  const observer = new MutationObserver(() => {
    findAndStyleButtons();
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Try immediately
  findAndStyleButtons();

  // Retry a few times with delays since shadow DOM may load later
  setTimeout(findAndStyleButtons, 500);
  setTimeout(findAndStyleButtons, 1000);
  setTimeout(findAndStyleButtons, 2000);
  setTimeout(findAndStyleButtons, 4000);

  // Also poll every second for 10 seconds to catch late-renders
  let pollCount = 0;
  const pollInterval = setInterval(() => {
    findAndStyleButtons();
    pollCount++;
    if (pollCount >= 10) clearInterval(pollInterval);
  }, 1000);
});

// ============================================
// Shop Pay Button Override (Product Pages)
// ============================================
document.addEventListener('DOMContentLoaded', function () {
  const seasonalColor = '#000000';

  function styleShopPayButton() {
    // Find all shop-pay-wallet-button elements
    const shopPayButtons = document.querySelectorAll('shop-pay-wallet-button');

    shopPayButtons.forEach((btn, idx) => {
      // Log all children to understand structure

      // Check for iframes inside
      const iframes = btn.querySelectorAll('iframe');
      if (iframes.length) {
        iframes.forEach((iframe, i) => {
          // Apply filter to iframe
          iframe.style.setProperty('filter', 'grayscale(1) contrast(100)', 'important');
        });
      }

      // FALLBACK: Apply CSS filter to shift purple to black
      if (!btn.shadowRoot && !btn._nousFilterApplied) {
        btn._nousFilterApplied = true;
        btn.style.setProperty('filter', 'grayscale(1) contrast(100)', 'important');

        // Also style parent container
        const container = btn.closest('.shopify-payment-button');
        if (container) {
          container.style.setProperty('isolation', 'isolate');
        }
      }

      // Try shadow root if available
      if (btn.shadowRoot) {
        // Target the purple background div
        const bgDiv = btn.shadowRoot.querySelector('div[class*="absolute"][class*="inset-y-0"][class*="-z-10"]');
        if (bgDiv) {
          bgDiv.style.setProperty('display', 'none', 'important');

          // Create replacement
          if (!btn.shadowRoot.querySelector('#nous-shoppay-bg')) {
            const replacement = document.createElement('div');
            replacement.id = 'nous-shoppay-bg';
            replacement.style.cssText = `
              position: absolute;
              inset: 0;
              z-index: -10;
              border-radius: 9999px;
              background-color: ${seasonalColor};
              pointer-events: none;
            `;
            bgDiv.parentNode.insertBefore(replacement, bgDiv);
          }
        }

        // Also try broader purple selector
        const purpleEls = btn.shadowRoot.querySelectorAll('[class*="purple"]');
        purpleEls.forEach((el) => {
          if (el.id !== 'nous-shoppay-bg') {
            el.style.setProperty('display', 'none', 'important');
          }
        });

        // Hide shop-pay-iframe-content and credit-card iframe inside shadow root
        btn.shadowRoot.querySelectorAll('shop-pay-iframe-content, .credit-card-iframe-visible').forEach((el) => {
          el.style.setProperty('display', 'none', 'important');
        });
      }
    });

    // Inject CSS into all open shadow roots to hide shop-pay-iframe-content
    if (!window._nousShopPaySheet) {
      window._nousShopPaySheet = new CSSStyleSheet();
      window._nousShopPaySheet.replaceSync('shop-pay-iframe-content, .credit-card-iframe-visible { display: none !important; }');
    }
    function injectSheetIntoShadowRoots(root) {
      root.querySelectorAll('*').forEach((el) => {
        if (el.shadowRoot) {
          const sheets = el.shadowRoot.adoptedStyleSheets;
          if (!sheets.includes(window._nousShopPaySheet)) {
            el.shadowRoot.adoptedStyleSheets = [...sheets, window._nousShopPaySheet];
          }
          injectSheetIntoShadowRoots(el.shadowRoot);
        }
      });
    }
    injectSheetIntoShadowRoots(document);
    // Also hide in light DOM
    document.querySelectorAll('shop-pay-iframe-content, .credit-card-iframe-visible').forEach((el) => {
      el.style.setProperty('display', 'none', 'important');
    });

    // Also apply to shopify-accelerated-checkout container
    const checkoutEls = document.querySelectorAll('shopify-accelerated-checkout');
    checkoutEls.forEach((checkout, idx) => {
      // Apply filter to the checkout element if no shadow access
      if (!checkout._nousFilterApplied) {
        checkout._nousFilterApplied = true;
        checkout.style.setProperty('filter', 'grayscale(1) contrast(100)', 'important');
      }

      if (checkout.shadowRoot) {
        const innerBtn = checkout.shadowRoot.querySelector('shop-pay-wallet-button');
        if (innerBtn) {
          if (!innerBtn.shadowRoot && !innerBtn._nousFilterApplied) {
            innerBtn._nousFilterApplied = true;
            innerBtn.style.setProperty('filter', 'grayscale(1) contrast(100)', 'important');
          }
        }
      }
    });
  }

  // Run immediately and with delays
  styleShopPayButton();
  setTimeout(styleShopPayButton, 500);
  setTimeout(styleShopPayButton, 1000);
  setTimeout(styleShopPayButton, 2000);
  setTimeout(styleShopPayButton, 3000);

  // Observer for dynamically added buttons
  const observer = new MutationObserver(() => {
    styleShopPayButton();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Poll for late shadow DOM
  let pollCount = 0;
  const pollInterval = setInterval(() => {
    styleShopPayButton();
    pollCount++;
    if (pollCount >= 15) clearInterval(pollInterval);
  }, 1000);
});

// Random tie-dye borders on cards
document.addEventListener('DOMContentLoaded', () => {
  const tieDyeClasses = ['tie-dye-top', 'tie-dye-bottom', 'tie-dye-both'];

  function applyRandomTieDye() {
    const cardInners = document.querySelectorAll('.card__inner:not([data-tie-dye-applied])');
    cardInners.forEach((card) => {
      const randomClass = tieDyeClasses[Math.floor(Math.random() * tieDyeClasses.length)];
      card.classList.add(randomClass);
      card.setAttribute('data-tie-dye-applied', 'true');
    });
  }

  // Run immediately
  applyRandomTieDye();

  // Observer for dynamically added cards
  const tieDyeObserver = new MutationObserver(() => {
    applyRandomTieDye();
  });
  tieDyeObserver.observe(document.body, { childList: true, subtree: true });
});

// Random video selector — runs synchronously on parse (deferred script, before DOMContentLoaded)
// Hides all but one section in any run of consecutive video sections.
// Works with 2, 3, or any number of consecutive video sections.
(function () {
  const sections = Array.from(document.querySelectorAll('.shopify-section'));
  let i = 0;
  while (i < sections.length) {
    if (!sections[i].querySelector('.video-section')) {
      i++;
      continue;
    }
    // collect the full consecutive group
    const groupStart = i;
    while (i < sections.length && sections[i].querySelector('.video-section')) i++;
    const group = sections.slice(groupStart, i);
    if (group.length < 2) continue;
    const winnerIdx = Math.floor(Math.random() * group.length);
    group.forEach((s, idx) => {
      if (idx !== winnerIdx) s.style.display = 'none';
    });
  }
})();
